/**
 * Posts a JSON body to a CSV-returning import endpoint.
 *
 * Uses `fetch` rather than the shared axios client because the response is a
 * CSV blob carrying its tallies in the `X-Metadata` header, which the import
 * wizard reads directly.
 */
export const postImport = (
  endpoint: string,
  data: any,
  accessToken: string,
  options?: { timeoutMs?: number }
) => {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  if (options?.timeoutMs) {
    timeoutId = setTimeout(() => controller.abort(), options.timeoutMs);
  }

  return fetch(process.env.REACT_APP_API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
    signal: controller.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const metadata = response.headers.get("X-Metadata");
      let parsedMetadata = null;
      if (metadata) {
        try {
          parsedMetadata = JSON.parse(metadata);
        } catch (e) {
          parsedMetadata = metadata;
        }
      }

      const blob = await response.blob();
      return { blob, metadata: parsedMetadata };
    })
    .finally(() => {
      if (timeoutId !== null) clearTimeout(timeoutId);
    });
};

/** Shape returned by every import endpoint, as consumed by the wizard. */
export type ImportFn = (
  data: any,
  accessToken: string,
  options?: { timeoutMs?: number }
) => Promise<{ blob: Blob; metadata: any }>;
